import { Button, DialogActions, TextField } from "@material-ui/core";
import moment from "moment";
import React, { Component } from "reactn";
import styled from "styled-components";

import { ASSET_RESOLUTION } from "common/constants";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import getUserProfile from "js/core/services/userProfiles";
import Avatar from "js/react/components/Avatar";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { BlueSwitch } from "js/react/components/Switch";
import TagsList from "js/react/components/TagsList";
import {
    BlueButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
} from "js/react/components/UiComponents";
import Tooltip from "js/react/views/PPTAddin/Components/Tooltip";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";

const logger = getLogger(LogGroup.ASSETS);

const AssetPreviewContainer = styled.div`
    width: 640px;
    height: 360px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #E0E0E0;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    .replace-button {
        opacity: 0;
    }

    &:hover {
        .replace-button {
            opacity: 1;
        }
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;

    label {
        font-size: 13px;
        width: 80px;
    }

    .avatar {
        margin-right: 10px;
    }

    .name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
    }

    .date {
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

export default class TeamAssetSettingsDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            assetName: "",
            alt: "",
            hidden: false,
            url: "",
            tags: [],
            createdBy: "",
            modifiedBy: "",
            fetching: true
        };
    }

    componentDidMount() {
        this.loadStateFromAsset(this.props.assetResource);
    }

    async loadStateFromAsset(assetResource) {
        try {
            const asset = await ds.assets.getAssetById(assetResource.resourceId);
            const url = await asset.getURL(ASSET_RESOLUTION.SMALL);

            const createdBy = await getUserProfile(assetResource.metadata.createdBy);
            const modifiedBy = await getUserProfile(assetResource.metadata.modifiedBy);
            this.setState({
                assetName: assetResource.metadata.name,
                alt: assetResource.metadata.alt,
                hidden: !assetResource.metadata.isPublished,
                tags: assetResource.tags ?? [],
                url,
                createdBy,
                modifiedBy,
                fetching: false
            });
        } catch (err) {
            logger.error(err, "[TeamAssetSettingsDialog] Error loading state from asset", { assetResource });

            ShowErrorDialog({
                title: "Error loading asset",
                message: "An error occurred while loading the asset. Please try again."
            });
        }
    }

    handleRemoveTag = tag => {
        let { tags } = this.state;

        if (tags) {
            tags = [...tags];
            const index = tags.indexOf(tag);
            if (index > -1) {
                tags.splice(index, 1);
                this.setState({ tags });
            }
        }
    }

    handleAddTag = () => {
        const { existingTags } = this.props;
        const { tags } = this.state;

        ShowDialog(AddTagsDialog, {
            activeTags: [...tags],
            existingTags,
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags,
                });
            },
        });
    }

    handleSaveChanges = async () => {
        const { assetResource, closeDialog } = this.props;
        const { assetName, hidden, tags, alt } = this.state;

        try {
            this.setState({ fetching: true });

            await WorkspaceController.updateWorkspaceResource({
                resourceId: assetResource.resourceId,
                resourceType: assetResource.resourceType,
                tags,
                metadata: {
                    name: assetName,
                    alt: alt,
                    isPublished: !hidden
                }
            });

            closeDialog();
        } catch (err) {
            logger.error(err, "[TeamAssetSettingsDialog] Error saving changes", { assetResource });

            ShowErrorDialog({
                title: "Error saving changes",
                message: "An error occurred while saving changes. Please try again."
            });
        }
    }

    render() {
        const { assetResource, closeDialog } = this.props;
        const { assetName, alt, hidden, createdBy, modifiedBy, url, tags, fetching } = this.state;

        return (
            <BeautifulDialog maxWidth="lg" closeDialog={closeDialog} closeButton>
                <FetchingClickShield visible={fetching} backgroundColor={"white"} />
                <UIPane style={{ overflow: "hidden" }}>
                    <UIPaneHeader>Team Asset Settings</UIPaneHeader>
                    <ScrollBox>
                        <UIPaneContents style={{ minHeight: 200 }}>
                            <Section>
                                <Gap10 />
                                <FlexBox top left>
                                    <AssetPreviewContainer
                                        title={alt}
                                        style={{ backgroundImage: `url(${url})` }}
                                    >
                                    </AssetPreviewContainer>
                                    <Gap30 />
                                    <FlexBox column top left width={500}>
                                        <TextField
                                            autoFocus
                                            id="library-name"
                                            label="Name"
                                            fullWidth
                                            value={assetName}
                                            onChange={event => this.setState({ assetName: event.target.value })}
                                            onBlur={event => this.setState({ assetName: event.target.value })}
                                        />
                                        <Gap30 />
                                        <UserInfo>
                                            <label>Created by</label>
                                            <Avatar url={createdBy.photoURL} />
                                            <div className="name">
                                                <span className="fullName">
                                                    {
                                                        createdBy.displayName ||
                                                        createdBy.email ||
                                                        <Tooltip title="This data predates our tracking of asset changes." arrow>
                                                            <b>Unknown</b>
                                                        </Tooltip>
                                                    }
                                                </span>
                                            </div>
                                            <div className="date">{moment.utc(assetResource.createdAt).local().format("LL")}</div>
                                        </UserInfo>
                                        <Gap20 />
                                        {modifiedBy &&
                                            <UserInfo>
                                                <label>Modified by</label>
                                                <Avatar url={modifiedBy.photoURL} />
                                                <div className="name">
                                                    <span className="fullName">
                                                        {
                                                            modifiedBy.displayName ||
                                                            modifiedBy.email ||
                                                            <Tooltip title="This data predates our tracking of asset changes." arrow>
                                                                <b>Unknown</b>
                                                            </Tooltip>
                                                        }
                                                    </span>
                                                </div>
                                                <div className="date">{moment.utc(assetResource.modifiedAt).local().format("LL")}</div>
                                            </UserInfo>
                                        }
                                        <Gap20 />
                                        <BlueSwitch
                                            id="hideSwitch"
                                            checked={!hidden}
                                            onChange={() => this.setState({ hidden: !hidden })}
                                            label="Published"
                                            tooltipTitle="Assets will be available for your team to use."
                                        />
                                        <Gap10 />
                                        <TextField
                                            id="alt-text"
                                            label="Alt text"
                                            fullWidth
                                            value={alt}
                                            onChange={event => this.setState({ alt: event.target.value })}
                                            onBlur={event => this.setState({ alt: event.target.value })}
                                        />
                                    </FlexBox>
                                </FlexBox>
                            </Section>
                            <Section>
                                <FlexBox left middle fillWidth>
                                    <div className="section-title">Tags</div>
                                    <Gap20 />
                                    <TagsList
                                        tags={tags}
                                        onRemove={this.handleRemoveTag}
                                        onClick={this.handleAddTag}
                                    />
                                </FlexBox>
                            </Section>
                        </UIPaneContents>
                    </ScrollBox>
                </UIPane>
                <DialogActions>
                    <FlexSpacer />
                    <Button onClick={closeDialog}>Cancel</Button>
                    <BlueButton onClick={this.handleSaveChanges}>Save Changes</BlueButton>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
